/**
 * Le parole dell’editor dei contenuti pensato per le persone: gruppi, elenchi,
 * testo formattato, etichette delle chiavi note (quelle ignote diventano «Camel case»).
 */
import type { DictNode } from './dict.js'

export const editorIt: DictNode = {
  editor: {
    headline: 'Titolo e immagine',
    status: 'Stato e pubblicazione',
    content: 'Contenuto',
    more: 'Altre informazioni',
    seo: 'Motori di ricerca (SEO)',
    translations: 'Traduzioni',
    tools: 'Strumenti AI',
    technical: 'Dettagli tecnici',
    technicalHelp: 'Informazioni di servizio, per esempio quelle dell’importazione dal vecchio sito. Si leggono qui ma non si modificano.',
    titleLabel: 'Titolo',
    titlePlaceholder: 'Scrivi il titolo',
    mainImage: 'Immagine principale',
    mainImageHelp: 'È la miniatura che compare negli elenchi e sui social.',
    emptyPlaceholder: 'Non compilato',
    writeHere: 'Scrivi qui…',
    urlPlaceholder: 'https://… oppure /pagina',
    noValue: '—',
    yes: 'Sì',
    no: 'No',
    addItem: 'Aggiungi',
    itemNumber: 'Elemento {{number}}',
    noItems: 'Nessun elemento. Aggiungine uno con il pulsante qui sotto.',
    removeItem: 'Rimuovi',
    moveUp: 'Sposta su',
    moveDown: 'Sposta giù',
    expand: 'Apri',
    collapse: 'Chiudi',
    filled: '{{count}} compilati',
    openLink: 'Apri il collegamento',
    dateLabel: 'Data',
    timeLabel: 'Ora',
    clearDate: 'Svuota la data',
    imageOrFile: 'Immagine o file',
    chooseImage: 'Scegli dalla libreria',
    changeImage: 'Cambia immagine',
    htmlStripped: 'Alcune parti tecniche del testo (come gli script) non si vedono qui e verranno tolte se modifichi il testo.',
    previewGoogle: 'Anteprima su Google',
    noDescription: 'Nessuna descrizione: i motori di ricerca ne sceglieranno una da soli.',
    translationsHelp: 'Lo stesso contenuto nelle altre lingue.',
    noTranslations: 'Nessuna traduzione collegata a questo contenuto.',
    openTranslation: 'Apri la traduzione',
    missingLocales: 'Mancano le traduzioni in: {{locales}}.',
    translationsLoadError: 'Non riesco a leggere le traduzioni in questo momento.',
    unsavedBanner: 'Hai modifiche non salvate in: {{fields}}.',
    savedOk: 'Modifiche salvate.',
    saveFailed: 'Non sono riuscito a salvare: {{reason}}',
    saveFailedGeneric: 'Non sono riuscito a salvare. Riprova tra poco.',
    discardAsk: 'Le modifiche non salvate andranno perse.',
    html: {
      bold: 'Grassetto',
      italic: 'Corsivo',
      h2: 'Titolo grande',
      h3: 'Titolo medio',
      paragraph: 'Paragrafo',
      bullets: 'Elenco puntato',
      numbers: 'Elenco numerato',
      quote: 'Citazione',
      link: 'Inserisci collegamento',
      unlink: 'Togli collegamento',
      image: 'Inserisci immagine',
      linkPrompt: 'Indirizzo del collegamento',
      linkApply: 'Applica',
      toolbar: 'Formattazione del testo',
      area: 'Testo formattato',
      placeholder: 'Scrivi il testo qui…',
    },
    keys: {
      title: 'Titolo',
      subtitle: 'Sottotitolo',
      eyebrow: 'Sopratitolo',
      description: 'Descrizione',
      ctaText: 'Testo del pulsante',
      ctaHref: 'Indirizzo del pulsante',
      ctaLabel: 'Testo del pulsante',
      cta: 'Pulsante',
      secondaryCta: 'Pulsante secondario',
      ctaSecondaryText: 'Testo del pulsante secondario',
      ctaSecondaryHref: 'Indirizzo del pulsante secondario',
      badgesHtml: 'Etichette in evidenza',
      badges: 'Etichette in evidenza',
      hero: 'Sezione principale',
      body: 'Testo',
      content: 'Contenuto',
      excerpt: 'Riassunto breve',
      summary: 'Riassunto',
      slug: 'Indirizzo breve',
      path: 'Indirizzo della pagina',
      href: 'Collegamento',
      url: 'Indirizzo web',
      link: 'Collegamento',
      image: 'Immagine',
      cover: 'Immagine di copertina',
      icon: 'Icona',
      icona: 'Icona',
      etichetta: 'Etichetta',
      titolo: 'Titolo',
      descrizione: 'Descrizione',
      collegamento: 'Collegamento',
      posizione: 'Posizione',
      keywords: 'Parole chiave',
      noindex: 'Nascondi ai motori di ricerca',
      canonical: 'Indirizzo canonico',
      author: 'Autore',
      tags: 'Argomenti',
      category: 'Categoria',
      publishedAt: 'Pubblicato il',
      updatedAt: 'Ultima modifica',
      createdAt: 'Creato il',
      alt: 'Testo alternativo',
      type: 'Tipo',
      assets: 'Immagini e allegati',
      extra: 'Altre informazioni',
      card: 'Scheda negli elenchi',
      landing: 'Struttura della pagina',
      seo: 'Motori di ricerca',
      meta: 'Motori di ricerca',
      ogTitle: 'Titolo sui social',
      ogDescription: 'Descrizione sui social',
      ogImage: 'Immagine sui social',
      items: 'Elementi',
      faq: 'Domande frequenti',
      faqItems: 'Domande frequenti',
      question: 'Domanda',
      answer: 'Risposta',
      label: 'Etichetta',
      name: 'Nome',
      text: 'Testo',
      price: 'Prezzo',
      features: 'Caratteristiche',
      sections: 'Sezioni',
      caption: 'Didascalia',
      background: 'Sfondo',
      order: 'Ordine',
      bodyFormat: 'Formato del testo',
      lineage: 'Provenienza',
      source: 'Origine',
      status: 'Stato',
      locale: 'Lingua',
      lang: 'Lingua',
      benefits: 'Vantaggi',
      steps: 'Passaggi',
      testimonials: 'Testimonianze',
      cards: 'Schede',
      buttons: 'Pulsanti',
      video: 'Video',
      gallery: 'Galleria',
      subtitleHtml: 'Sottotitolo',
      bodyHtml: 'Testo',
      html: 'Testo',
      headline: 'Titolo principale',
      tagline: 'Motto',
      robots: 'Indicazioni per i motori di ricerca',
      role: 'Ruolo',
      company: 'Azienda',
      quote: 'Citazione',
      value: 'Valore',
      duration: 'Durata',
      level: 'Livello',
      date: 'Data',
      readingTime: 'Tempo di lettura',
      target: 'Apertura del collegamento',
    },
  },
}

export const editorEn: DictNode = {
  editor: {
    headline: 'Title and image',
    status: 'Status and publishing',
    content: 'Content',
    more: 'More information',
    seo: 'Search engines (SEO)',
    translations: 'Translations',
    tools: 'AI tools',
    technical: 'Technical details',
    technicalHelp: 'Housekeeping information, for example from the import from the old site. You can read it here but not change it.',
    titleLabel: 'Title',
    titlePlaceholder: 'Write the title',
    mainImage: 'Main image',
    mainImageHelp: 'The thumbnail shown in lists and on social networks.',
    emptyPlaceholder: 'Not filled in',
    writeHere: 'Write here…',
    urlPlaceholder: 'https://… or /page',
    noValue: '—',
    yes: 'Yes',
    no: 'No',
    addItem: 'Add',
    itemNumber: 'Item {{number}}',
    noItems: 'No items. Add one with the button below.',
    removeItem: 'Remove',
    moveUp: 'Move up',
    moveDown: 'Move down',
    expand: 'Open',
    collapse: 'Close',
    filled: '{{count}} filled in',
    openLink: 'Open the link',
    dateLabel: 'Date',
    timeLabel: 'Time',
    clearDate: 'Clear the date',
    imageOrFile: 'Image or file',
    chooseImage: 'Choose from the library',
    changeImage: 'Change image',
    htmlStripped: 'Some technical parts of the text (such as scripts) are not shown here and will be removed if you edit the text.',
    previewGoogle: 'Preview on Google',
    noDescription: 'No description: search engines will pick one themselves.',
    translationsHelp: 'The same content in the other languages.',
    noTranslations: 'No translation is linked to this content.',
    openTranslation: 'Open the translation',
    missingLocales: 'Missing translations in: {{locales}}.',
    translationsLoadError: 'I cannot read the translations right now.',
    unsavedBanner: 'You have unsaved changes in: {{fields}}.',
    savedOk: 'Changes saved.',
    saveFailed: 'I could not save: {{reason}}',
    saveFailedGeneric: 'I could not save. Please try again shortly.',
    discardAsk: 'Unsaved changes will be lost.',
    html: {
      bold: 'Bold',
      italic: 'Italic',
      h2: 'Large heading',
      h3: 'Medium heading',
      paragraph: 'Paragraph',
      bullets: 'Bulleted list',
      numbers: 'Numbered list',
      quote: 'Quote',
      link: 'Insert link',
      unlink: 'Remove link',
      image: 'Insert image',
      linkPrompt: 'Link address',
      linkApply: 'Apply',
      toolbar: 'Text formatting',
      area: 'Formatted text',
      placeholder: 'Write the text here…',
    },
    keys: {
      title: 'Title',
      subtitle: 'Subtitle',
      eyebrow: 'Eyebrow',
      description: 'Description',
      ctaText: 'Button text',
      ctaHref: 'Button address',
      ctaLabel: 'Button text',
      cta: 'Button',
      secondaryCta: 'Secondary button',
      ctaSecondaryText: 'Secondary button text',
      ctaSecondaryHref: 'Secondary button address',
      badgesHtml: 'Highlighted badges',
      badges: 'Highlighted badges',
      hero: 'Main section',
      body: 'Text',
      content: 'Content',
      excerpt: 'Short summary',
      summary: 'Summary',
      slug: 'Short address',
      path: 'Page address',
      href: 'Link',
      url: 'Web address',
      link: 'Link',
      image: 'Image',
      cover: 'Cover image',
      icon: 'Icon',
      icona: 'Icon',
      etichetta: 'Label',
      titolo: 'Title',
      descrizione: 'Description',
      collegamento: 'Link',
      posizione: 'Position',
      keywords: 'Keywords',
      noindex: 'Hide from search engines',
      canonical: 'Canonical address',
      author: 'Author',
      tags: 'Topics',
      category: 'Category',
      publishedAt: 'Published on',
      updatedAt: 'Last changed',
      createdAt: 'Created on',
      alt: 'Alternative text',
      type: 'Type',
      assets: 'Images and files',
      extra: 'More information',
      card: 'Card in listings',
      landing: 'Page structure',
      seo: 'Search engines',
      meta: 'Search engines',
      ogTitle: 'Social title',
      ogDescription: 'Social description',
      ogImage: 'Social image',
      items: 'Items',
      faq: 'Frequently asked questions',
      faqItems: 'Frequently asked questions',
      question: 'Question',
      answer: 'Answer',
      label: 'Label',
      name: 'Name',
      text: 'Text',
      price: 'Price',
      features: 'Features',
      sections: 'Sections',
      caption: 'Caption',
      background: 'Background',
      order: 'Order',
      bodyFormat: 'Text format',
      lineage: 'Provenance',
      source: 'Source',
      status: 'Status',
      locale: 'Language',
      lang: 'Language',
      benefits: 'Benefits',
      steps: 'Steps',
      testimonials: 'Testimonials',
      cards: 'Cards',
      buttons: 'Buttons',
      video: 'Video',
      gallery: 'Gallery',
      subtitleHtml: 'Subtitle',
      bodyHtml: 'Text',
      html: 'Text',
      headline: 'Headline',
      tagline: 'Tagline',
      robots: 'Search engine directives',
      role: 'Role',
      company: 'Company',
      quote: 'Quote',
      value: 'Value',
      duration: 'Duration',
      level: 'Level',
      date: 'Date',
      readingTime: 'Reading time',
      target: 'Link target',
    },
  },
}
